/**
 * Sandboxes admin page — list + delete agent workspaces.
 *
 * Each agent workspace maps to one Cloudflare Sandbox container DO.
 * Deleting a workspace cascades to its sessions and their messages.
 * The container itself goes idle and shuts down within ~10 min via
 * the workspace provider's `sleepAfter` window; the DO instance and
 * its (mostly empty) storage become orphan-but-inert.
 *
 * Mirrors the DLQ page's shape: filter bar, table, per-row delete
 * with `ReasonModal`, plus a bulk-delete action footer.
 */

import { useEffect, useState } from 'react';
import { adminApi, type AdminSandboxRow } from '@/api/client';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ReasonModal } from '@/components/ReasonModal';
import { useErrorToast, useToast } from '@/lib/toast';
import { formatTime, timeAgo } from '@/util/format';

type Confirm =
  | { kind: 'delete'; row: AdminSandboxRow }
  | { kind: 'bulk'; scope: 'org' | 'all'; orgId?: string }
  | null;

export function SandboxesPage() {
  const [orgId, setOrgId] = useState('');
  const [rows, setRows] = useState<AdminSandboxRow[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<Confirm>(null);
  const onError = useErrorToast();
  const toast = useToast();

  async function refresh() {
    setBusy(true);
    try {
      const r = await adminApi.listSandboxes({
        orgId: orgId.trim() || undefined,
        limit: 200,
      });
      setRows(r.data);
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pre-computed for the bulk-delete buttons in the footer.
  const totalSessions = rows?.reduce((sum, r) => sum + r.sessionCount, 0) ?? 0;

  return (
    <>
      <div>
        <h1 className="text-2xl font-semibold">Sandboxes</h1>
        <p className="text-sm text-muted-foreground">
          Agent workspaces, one row per Cloudflare Sandbox container. Deletion cascades to all
          per-workspace sessions, messages, attachments, file edits, and audit events. Container DOs
          go idle and shut down on their own within ~10 minutes.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto]">
        <Input
          placeholder="Filter by org id (leave blank for all orgs)"
          value={orgId}
          onChange={(e) => setOrgId(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              void refresh();
            }
          }}
        />
        <Button disabled={busy} onClick={() => void refresh()}>
          {busy ? 'Loading…' : 'Search'}
        </Button>
        <span className="self-center text-sm text-muted-foreground">
          {rows ? `${rows.length} workspaces · ${totalSessions} sessions` : ''}
        </span>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Updated</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Org</TableHead>
              <TableHead>Provider</TableHead>
              <TableHead>Sessions</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows?.map((r) => (
              <TableRow key={r.id}>
                <TableCell
                  className="whitespace-nowrap text-muted-foreground"
                  title={formatTime(r.updatedAt)}
                >
                  {timeAgo(r.updatedAt)}
                </TableCell>
                <TableCell>
                  <div className="font-medium">{r.name}</div>
                  <div className="font-mono text-[11px] text-muted-foreground">{r.id}</div>
                </TableCell>
                <TableCell>
                  <div className="font-medium">{r.organizationName ?? '—'}</div>
                  <div className="font-mono text-[11px] text-muted-foreground">
                    {r.organizationId ?? 'no org'}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="font-mono text-[11px]">
                    {r.workspaceProviderId}
                  </Badge>
                </TableCell>
                <TableCell>{r.sessionCount}</TableCell>
                <TableCell
                  className="whitespace-nowrap text-muted-foreground"
                  title={formatTime(r.createdAt)}
                >
                  {timeAgo(r.createdAt)}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => setConfirm({ kind: 'delete', row: r })}
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {rows && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="h-24 text-center text-muted-foreground">
                  No sandboxes match the filter.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-md border border-destructive/30 bg-destructive/5 p-3">
        <span className="text-sm font-medium text-destructive">Danger zone</span>
        <span className="text-xs text-muted-foreground">
          Bulk delete cascades everything; recovery means rebuilding from scratch.
        </span>
        <div className="ml-auto flex gap-2">
          <Button
            size="sm"
            variant="destructive"
            disabled={!orgId.trim()}
            onClick={() => setConfirm({ kind: 'bulk', scope: 'org', orgId: orgId.trim() })}
          >
            Delete all in org
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={() => setConfirm({ kind: 'bulk', scope: 'all' })}
          >
            Delete EVERY workspace
          </Button>
        </div>
      </div>

      <ReasonModal
        open={confirm?.kind === 'delete'}
        title="Delete this workspace?"
        description={
          confirm?.kind === 'delete'
            ? `Cascades to ${confirm.row.sessionCount} session(s) and all their messages, attachments, file edits, and audit events. The Cloudflare Sandbox DO goes idle on its own.`
            : ''
        }
        confirmLabel="Delete"
        confirmKind="destructive"
        onOpenChange={(o) => !o && setConfirm(null)}
        onConfirm={async (reason) => {
          if (confirm?.kind !== 'delete') {
            return;
          }
          try {
            await adminApi.deleteSandbox(confirm.row.id, reason);
            toast.success('Workspace deleted.');
            await refresh();
          } catch (e) {
            onError(e);
          }
        }}
      />

      <ReasonModal
        open={confirm?.kind === 'bulk'}
        title={
          confirm?.kind === 'bulk' && confirm.scope === 'all'
            ? 'Delete EVERY workspace in this environment?'
            : 'Delete every workspace in this org?'
        }
        description={
          confirm?.kind === 'bulk' && confirm.scope === 'all'
            ? 'This will wipe all agent workspaces and sessions across every org. There is no undo.'
            : confirm?.kind === 'bulk'
              ? `Wipes every workspace and session for org ${confirm.orgId}. There is no undo.`
              : ''
        }
        confirmLabel="Delete all"
        confirmKind="destructive"
        onOpenChange={(o) => !o && setConfirm(null)}
        onConfirm={async (reason) => {
          if (confirm?.kind !== 'bulk') {
            return;
          }
          try {
            const r = await adminApi.bulkDeleteSandboxes({
              scope: confirm.scope,
              orgId: confirm.scope === 'org' ? confirm.orgId : undefined,
              reason,
            });
            toast.success(`Deleted ${r.workspacesDeleted} workspace(s).`);
            await refresh();
          } catch (e) {
            onError(e);
          }
        }}
      />
    </>
  );
}
